'use client';

import { MANUAL_PDF_URL } from '@/lib/manual-version';
import { fetchMeClient } from '@/lib/me-client';
import { Globe2, Menu, Palette, Type } from 'lucide-react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState, useTransition } from 'react';

/**
 * V2.11 (2026-05-21g) — unified profile dropdown.
 *
 * Per Leon: right-corner avatar opens a hamburger-style dropdown
 * with language toggle, logout, future Theme; admin gets a
 * 用户视图 / 管理员视图 switcher inline.
 *
 * One component, two callers:
 *   - <TopBarClient /> on desktop (right side of the top bar)
 *   - <WorkspaceMobileHeader /> + <MobileTopChrome /> on mobile
 *
 * Variants:
 *   - compact (mobile, default): circular avatar with initial only
 *   - full (desktop): avatar + display_name + chevron
 *
 * Auth state comes from initialMe (server-seeded). Client re-fetches
 * /api/me on mount as a defense against next-on-pages stale layout
 * caching — same pattern as the legacy TopBarClient. Anonymous
 * variant shows a "登录" link + language toggle, no profile chrome.
 */

export type ProfileMe = {
  user: {
    email: string;
    role: string;
    display_name: string | null;
    username: string | null;
  };
};

export type ProfileMenuDict = {
  login: string;
  signup: string;
  logout: string;
  my_overview: string;
  share_settings: string;
  locale_toggle_to: string;
  language_label: string;
  view_label: string;
  view_user: string;
  view_admin: string;
  theme_label: string;
  theme_soon: string;
};

export function ProfileMenu({
  initialMe,
  locale,
  dict,
  variant = 'compact',
}: {
  initialMe: ProfileMe | null;
  locale: 'zh' | 'en';
  dict: ProfileMenuDict;
  variant?: 'compact' | 'full';
}) {
  const router = useRouter();
  const pathname = usePathname() ?? '';
  const [me, setMe] = useState<ProfileMe | null>(initialMe);
  const [open, setOpen] = useState(false);
  const [, startTransition] = useTransition();
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Same defense as TopBarClient: re-check /api/me on mount in case
  // the server seed was stale (next-on-pages 1.13 layout caching).
  // V3.4 (2026-06-03 P2-C) — shared via fetchMeClient() so the three
  // ProfileMenu mounts on one page (desktop TopBarClient + mobile
  // WorkspaceMobileHeader + mobile MobileTopChrome) all reuse one
  // /api/me round-trip instead of fanning out to three.
  //
  // V3.4 (2026-06-04) — skip the defensive fetch when the server seed
  // is non-null. The stale-layout corner case only matters when the
  // server says "anonymous" but the user is actually signed in;
  // when initialMe is already populated, the seed is trustworthy and
  // the extra /api/me round-trip is pure waste on every nav. Still
  // re-fetches when initialMe is null (the original recovery path).
  useEffect(() => {
    if (initialMe) return;
    let cancelled = false;
    fetchMeClient().then((body) => {
      if (cancelled) return;
      if (body) setMe(body as ProfileMe);
      else setMe(null);
    });
    return () => {
      cancelled = true;
    };
  }, [initialMe]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Close whenever the route changes (e.g. user picked "我的概览").
  // biome-ignore lint/correctness/useExhaustiveDependencies: close on path change only
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const role = me?.user.role ?? null;
  const isAdmin = role === 'platform_admin';
  const isEconOrAdmin = role === 'economist' || role === 'platform_admin';
  const inAdminView = pathname.startsWith('/platform');
  const initial = (me?.user.display_name ?? me?.user.email ?? '?').charAt(0).toUpperCase();

  async function switchLocale() {
    const next: 'zh' | 'en' = locale === 'zh' ? 'en' : 'zh';
    try {
      await fetch('/api/locale', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ locale: next }),
      });
    } catch {
      // best-effort; refresh below still re-reads cookie state
    }
    startTransition(() => router.refresh());
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={me ? '账号菜单' : '语言与登录'}
        className={`inline-flex items-center ${
          variant === 'full'
            ? 'gap-2 pl-1.5 pr-3.5 py-1.5 border border-soft rounded-full bg-surface hover:border-gold-soft'
            : // V2.11 (2026-05-28g) — compact variant centers its inner
              // avatar circle. Previously it relied on gap-2 from the
              // shared class, so the navy circle sat at the left edge of
              // the white outer circle (the "big-circle-wrapping-small-
              // circle, asymmetrical" thing Leon spotted on mobile login).
              // AXQA-42-05 — was w-9 h-9 (36px). Apple HIG min tap target
              // is 44px; bumped to w-11 h-11 so the menu button is easier
              // to hit on phones without overflowing the chrome.
              'justify-center w-11 h-11 rounded-full bg-white/90 backdrop-blur border border-soft shadow-sm hover:border-gold-soft'
        } transition`}
      >
        <span
          className={`${
            variant === 'full' ? 'w-7 h-7' : 'w-7 h-7'
          } rounded-full bg-navy text-gold-soft flex items-center justify-center font-serif font-semibold text-[13px] flex-shrink-0`}
          aria-hidden
        >
          {me ? initial : <Menu size={16} strokeWidth={1.75} />}
        </span>
        {variant === 'full' && me && (
          <>
            <span className="text-[13px] text-navy max-w-[140px] truncate">
              {me.user.display_name ?? me.user.email.split('@')[0]}
            </span>
            <span aria-hidden className="text-text-mute text-xs">
              ▾
            </span>
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-60 bg-white border border-soft rounded-2xl shadow-xl overflow-hidden z-50"
        >
          {me ? (
            <>
              <div className="px-4 py-3 bg-cream/60 border-b border-soft">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-navy truncate">
                    {me.user.display_name ?? me.user.email.split('@')[0]}
                  </p>
                  {/* CRO-65 P2.9 (2026-06-05) — surface platform_admin
                      identity at the top of the dropdown so testers /
                      anyone unsure stops asking "为什么我看到 admin 文案".
                      Also annotates which view they're in (用户/管理). */}
                  {isAdmin && (
                    <span
                      className="font-mono text-[9.5px] tracking-[0.16em] uppercase text-gold-3 border border-gold-3/40 rounded-r-xs px-1.5 py-0.5 leading-none whitespace-nowrap"
                      title={inAdminView ? '管理视图' : '用户视图'}
                    >
                      超级管理员 · {inAdminView ? '管理' : '用户'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-dusk truncate mt-0.5">{me.user.email}</p>
              </div>

              {isEconOrAdmin && (
                <>
                  <MenuLink href="/studio">{dict.my_overview}</MenuLink>
                  {/* CRO-63 P1 #7 (2026-06-04) — 分享设置 now anchors to
                      /studio/launch#visibility so the menu item lands on
                      the "选择可见性" section instead of the stale
                      /studio/share legacy route that didn't navigate on
                      staging. */}
                  <MenuLink href="/studio/launch#visibility">{dict.share_settings}</MenuLink>
                  {/* V3.4 (2026-06-03n) — 「入驻信息」 (was 「我的入驻」).
                      Shown for both economists AND platform admins; the
                      step machine carves out /onboarding/role +
                      /onboarding/apply + /onboarding/waiting from the
                      admin-redirect-to-platform rule so the link
                      actually lands on the page (read-only view of
                      whatever data exists; admin's own account
                      typically has no application, which the page
                      surfaces as an empty state). */}
                  <MenuLink href="/onboarding/role">入驻信息</MenuLink>
                  {/* 2026-07-06 (password self-service) — set/change
                      password so users who prefer email + password
                      login over per-session code have a discoverable
                      entry point. Opt-in; the default flow (email
                      code) still works without this. */}
                  <MenuLink href="/studio/settings/security">安全设置</MenuLink>
                  <Divider />
                </>
              )}

              {isAdmin && (
                <>
                  <ViewSwitchRow
                    label={dict.view_label}
                    current={inAdminView ? 'admin' : 'user'}
                    userLabel={dict.view_user}
                    adminLabel={dict.view_admin}
                  />
                  <Divider />
                </>
              )}

              <LanguageRow
                label={dict.language_label}
                current={locale}
                switchToLabel={dict.locale_toggle_to}
                onSwitch={switchLocale}
              />
              <FontScaleRow />
              <ThemeRow label={dict.theme_label} soon={dict.theme_soon} />

              <Divider />
              {/* Leon ask (2026-06-28) — surface 用户手册 here so already-
                  onboarded users can re-open the PDF even after the
                  first-time guide card on /studio has been dismissed. */}
              <a
                href={MANUAL_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-2.5 text-sm text-ink hover:bg-cream/70 transition"
              >
                📖 用户手册 ↗
              </a>
              <Divider />
              <form action="/api/auth/logout" method="POST">
                <button
                  type="submit"
                  className="w-full text-left px-4 py-2.5 text-sm text-warmth-dark hover:bg-warmth/10 transition"
                >
                  {dict.logout}
                </button>
              </form>
            </>
          ) : (
            <>
              <MenuLink href="/login">{dict.login}</MenuLink>
              <MenuLink href="/signup">{dict.signup}</MenuLink>
              <Divider />
              <LanguageRow
                label={dict.language_label}
                current={locale}
                switchToLabel={dict.locale_toggle_to}
                onSwitch={switchLocale}
              />
              <Divider />
              <a
                href={MANUAL_PDF_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block px-4 py-2.5 text-sm text-ink hover:bg-cream/70 transition"
              >
                📖 用户手册 ↗
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MenuLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      role="menuitem"
      className="block px-4 py-2.5 text-sm text-dusk hover:bg-cream/60 hover:text-navy transition"
    >
      {children}
    </Link>
  );
}

function Divider() {
  return <div className="border-t border-soft/70" />;
}

function LanguageRow({
  label,
  current,
  switchToLabel,
  onSwitch,
}: {
  label: string;
  current: 'zh' | 'en';
  switchToLabel: string;
  onSwitch: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSwitch}
      role="menuitem"
      className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-dusk hover:bg-cream/60 hover:text-navy transition"
    >
      <span className="flex items-center gap-2">
        <Globe2 aria-hidden size={15} strokeWidth={1.75} className="text-dusk" />
        <span>{label}</span>
      </span>
      <span className="inline-flex items-center gap-1 text-xs">
        <span className="text-ink font-mono">{current === 'zh' ? '中' : 'EN'}</span>
        <span aria-hidden className="text-text-mute">
          →
        </span>
        <span className="text-warmth font-mono">{switchToLabel}</span>
      </span>
    </button>
  );
}

function ViewSwitchRow({
  label,
  current,
  userLabel,
  adminLabel,
}: {
  label: string;
  current: 'user' | 'admin';
  userLabel: string;
  adminLabel: string;
}) {
  return (
    <div className="px-4 py-2.5">
      <p className="text-[11px] uppercase tracking-wider text-text-mute mb-1.5">{label}</p>
      <div className="inline-flex items-center rounded-full border border-soft bg-cream/40 p-0.5 text-xs font-medium w-full">
        <Link
          href="/studio"
          role="menuitem"
          className={`flex-1 text-center px-3 py-1.5 rounded-full transition ${
            current === 'user' ? 'bg-navy text-gold-light shadow-sm' : 'text-dusk hover:text-navy'
          }`}
        >
          {userLabel}
        </Link>
        <Link
          href="/platform"
          role="menuitem"
          className={`flex-1 text-center px-3 py-1.5 rounded-full transition ${
            current === 'admin' ? 'bg-navy text-gold-light shadow-sm' : 'text-dusk hover:text-navy'
          }`}
        >
          {adminLabel}
        </Link>
      </div>
    </div>
  );
}

function ThemeRow({ label, soon }: { label: string; soon: string }) {
  return (
    <div
      aria-disabled="true"
      className="flex items-center justify-between px-4 py-2.5 text-sm text-text-mute select-none"
    >
      <span className="flex items-center gap-2">
        <Palette aria-hidden size={15} strokeWidth={1.75} className="text-text-mute" />
        <span>{label}</span>
      </span>
      <span className="text-[11px] italic">{soon}</span>
    </div>
  );
}

/**
 * 2026-06-15 — font-scale picker. Three presets that translate to the
 * root <html> font-size; tailwind tokens (text-xs, text-sm, etc.) use rem
 * so everything scales proportionally. Persisted via localStorage and
 * applied by the inline script in layout.tsx before first paint.
 */
type FontScale = 'standard' | 'comfortable' | 'large';

function FontScaleRow() {
  const [scale, setScale] = useState<FontScale>('standard');
  useEffect(() => {
    try {
      const s = localStorage.getItem('font-scale');
      if (s === 'comfortable' || s === 'large') setScale(s);
    } catch {
      // localStorage blocked — leave at standard.
    }
  }, []);
  function apply(next: FontScale) {
    setScale(next);
    try {
      if (next === 'standard') localStorage.removeItem('font-scale');
      else localStorage.setItem('font-scale', next);
    } catch {
      /* ignore */
    }
    const root = document.documentElement;
    if (next === 'standard') root.removeAttribute('data-font-scale');
    else root.setAttribute('data-font-scale', next);
  }
  const options: { key: FontScale; label: string; px: string }[] = [
    { key: 'standard', label: '标准', px: '16' },
    { key: 'comfortable', label: '舒适', px: '17.5' },
    { key: 'large', label: '大字', px: '19' },
  ];
  return (
    <div className="px-4 py-2.5 border-b border-soft/70">
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-2 text-sm text-dusk">
          <Type aria-hidden size={15} strokeWidth={1.75} className="text-dusk" />
          <span>字号</span>
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1">
        {options.map((o) => {
          const active = o.key === scale;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => apply(o.key)}
              className={`text-center py-1.5 rounded-md border transition ${
                active
                  ? 'bg-ink text-paper border-ink'
                  : 'bg-white text-ink-mute border-soft hover:border-gold-3/60 hover:text-ink'
              }`}
              aria-pressed={active}
            >
              <div className="text-[12px] leading-none">{o.label}</div>
              <div className="text-[9.5px] tracking-[0.05em] mt-0.5 opacity-70">{o.px}px</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
